import { SubContentProps } from '@/components/MenuDrawer/MyMenuDrawer/SubMenu/LanguageSetting';
import {
  PortalNoti,
  updateMyPortalOption,
  updateMyPortalOptions,
  useMyPortalNotiList,
} from '@/hooks/apiHooks';
import * as React from 'react';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { ApprovalAlarmSettingProps } from '@/components/MenuDrawer/MyMenuDrawer/SubMenu/AlarmSetting/Approval';
import { useSnackbar } from '@/hooks/global-tools';
import { useTranslation } from 'react-i18next';

// 팀원의 근태 문서 — 단일 키 on/off (라디오 없음)
const ATTENDANCE_KEYS = [
  'approval_confirm_annual_leave_occurred',
  'approval_confirm_working_hours_changed',
  'approval_confirm_overtime_occurred',
  'approval_confirm_commute_adjusted',
] as const;

const getActiveCategories = (approval: PortalNoti[]): string[] => {
  const alarmCategory = {
    의견추가: ['approval_opinion_attach'],
    보류: ['approval_hold', 'approval_hold_by_opinion'],
    최종승인: [
      'approval_confirm',
      'approval_confirm_by_opinion',
      'approval_confirm_referee',
      'approval_confirm_referee_by_opinion',
    ],
    반려: ['approval_reject'],
    결재: ['approval_process'],
    반려요청: ['approval_reject_request'],
    참조: ['approval_add_referee_by_opinion', 'approval_add_referee'],
    취소: ['approval_cancel', 'approval_cancel_referee'],
    결재지연: ['approval_delay_reminder'],
    연차발생: ['approval_confirm_annual_leave_occurred'],
    근무시간변경: ['approval_confirm_working_hours_changed'],
    연장근무발생: ['approval_confirm_overtime_occurred'],
    근태조정: ['approval_confirm_commute_adjusted'],
  };

  const activeKeys = approval
    .filter((item) => item.webActivated)
    .map((item) => item.templateKey);

  return Object.entries(alarmCategory)
    .filter(([_, keys]) => keys.some((key) => activeKeys.includes(key)))
    .map(([category]) => category);
};

const useAlarmSettingProps = (
  settings: SubContentProps,
): ApprovalAlarmSettingProps & {
  readonly approvalSummary: React.ReactNode;
  readonly isJiraActivated: boolean;
  readonly jiraServiceName: string;
  readonly onClickApprovalMenu: () => void;
} => {
  const { t } = useTranslation();
  const { data, mutate: updateNotiList } = useMyPortalNotiList();
  const { showSuccess, showError } = useSnackbar();

  const [isApprovalOpen, setIsApprovalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [switchValue, setSwitchValue] = useState<
    ApprovalAlarmSettingProps['approvalValues']
  >({
    all: false,
    approval_opinion_attach: false,
    approval_hold: false,
    approval_confirm: false,
    approval_reject: false,
    approval_process: false,
    approval_reject_request: false,
    approval_add_referee: false,
    approval_cancel: false,
    approval_cancel_referee: false,
    approval_delay_reminder: false,
    approval_confirm_referee: false,
    approval_confirm_annual_leave_occurred: false,
    approval_confirm_working_hours_changed: false,
    approval_confirm_overtime_occurred: false,
    approval_confirm_commute_adjusted: false,
    approval_hold_value: 'always',
    approval_confirm_value: 'always',
    approval_add_referee_value: 'always',
    approval_confirm_referee_value: 'always',
  });

  const jiraActivated = data?.jira?.[0]?.webActivated ?? false;
  const jiraServiceName = data?.jira?.[0]?.description ?? 'IT Service';

  const approvalSummary = useMemo(() => {
    if (data?.approval === undefined) {
      return '';
    }

    const filteredItem = getActiveCategories(data.approval);
    switch (filteredItem.length) {
      case 0: {
        return t('MYPAGE.모든 알림이 꺼져있어요.');
      }
      case 1:
      case 2: {
        return (
          <>
            {filteredItem.map((service) => t(`MYPAGE.${service}`)).join(', ')}
            <br />
            <div
              dangerouslySetInnerHTML={{
                __html: t('MYPAGE.{{n}}개 알림이 켜져 있어요.', {
                  n: filteredItem.length,
                }).replace(/([\d.,]+)/g, '<span class="blue">$&</span>'),
              }}
            />
          </>
        );
      }
      default: {
        return (
          <>
            {filteredItem
              .slice(0, 3)
              .map((service) => t(`MYPAGE.${service}`))
              .join(', ')}
            {t('MYPAGE. 등')}
            <br />
            <div
              dangerouslySetInnerHTML={{
                __html: t('MYPAGE.{{n}}개 알림이 켜져 있어요.', {
                  n: filteredItem.length,
                }).replace(/([\d.,]+)/g, '<span class="blue">$&</span>'),
              }}
            />
          </>
        );
      }
    }
  }, [data?.approval]);

  const onClickApprovalMenu = () => {
    setIsApprovalOpen(true);
  };

  useEffect(() => {
    if (data?.approval === undefined) {
      return;
    }

    const isItOn = (keyName: string) => {
      return (
        data.approval.filter(
          (item) => item.webActivated && item.templateKey === keyName,
        )?.length > 0
      );
    };

    // 라디오를 가진 항목은 always/by_opinion 중 하나라도 켜져 있으면 ON
    const approval_hold =
      isItOn('approval_hold') || isItOn('approval_hold_by_opinion');
    const approval_confirm =
      isItOn('approval_confirm') || isItOn('approval_confirm_by_opinion');
    const approval_add_referee =
      isItOn('approval_add_referee') ||
      isItOn('approval_add_referee_by_opinion');

    const approval_opinion_attach = isItOn('approval_opinion_attach');
    const approval_reject = isItOn('approval_reject');
    const approval_process = isItOn('approval_process');
    const approval_reject_request = isItOn('approval_reject_request');
    const approval_cancel = isItOn('approval_cancel');
    const approval_cancel_referee = isItOn('approval_cancel_referee');
    const approval_delay_reminder = isItOn('approval_delay_reminder');
    const approval_confirm_referee =
      isItOn('approval_confirm_referee') ||
      isItOn('approval_confirm_referee_by_opinion');

    const approval_confirm_annual_leave_occurred = isItOn(
      'approval_confirm_annual_leave_occurred',
    );
    const approval_confirm_working_hours_changed = isItOn(
      'approval_confirm_working_hours_changed',
    );
    const approval_confirm_overtime_occurred = isItOn(
      'approval_confirm_overtime_occurred',
    );
    const approval_confirm_commute_adjusted = isItOn(
      'approval_confirm_commute_adjusted',
    );

    setSwitchValue({
      all:
        approval_hold &&
        approval_confirm &&
        approval_add_referee &&
        approval_opinion_attach &&
        approval_reject &&
        approval_process &&
        approval_reject_request &&
        approval_cancel &&
        approval_cancel_referee &&
        approval_delay_reminder &&
        approval_confirm_referee &&
        approval_confirm_annual_leave_occurred &&
        approval_confirm_working_hours_changed &&
        approval_confirm_overtime_occurred &&
        approval_confirm_commute_adjusted,
      approval_opinion_attach,
      approval_hold,
      approval_confirm,
      approval_reject,
      approval_process,
      approval_reject_request,
      approval_add_referee,
      approval_cancel,
      approval_cancel_referee,
      approval_delay_reminder,
      approval_confirm_referee,
      approval_confirm_annual_leave_occurred,
      approval_confirm_working_hours_changed,
      approval_confirm_overtime_occurred,
      approval_confirm_commute_adjusted,
      approval_hold_value: approval_hold
        ? isItOn('approval_hold')
          ? 'always'
          : 'by_opinion'
        : 'always',
      approval_confirm_value: approval_confirm
        ? isItOn('approval_confirm')
          ? 'always'
          : 'by_opinion'
        : 'always',
      approval_add_referee_value: approval_add_referee
        ? isItOn('approval_add_referee')
          ? 'always'
          : 'by_opinion'
        : 'always',
      approval_confirm_referee_value: approval_confirm_referee
        ? isItOn('approval_confirm_referee')
          ? 'always'
          : 'by_opinion'
        : 'always',
    });
  }, [data?.approval]);

  const handleChangeAlarm = async (
    event: ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => {
    const approval_opinion_attach = (checked: boolean) => {
      if (checked) {
        turnOnList.push('approval_opinion_attach');
      } else {
        turnOffList.push('approval_opinion_attach');
      }
    };
    const approval_hold = (checked: boolean) => {
      //approval_hold, approval_hold_by_opinion
      if (checked) {
        turnOnList.push('approval_hold', 'approval_hold_by_opinion');
      } else {
        turnOffList.push('approval_hold', 'approval_hold_by_opinion');
      }
    };
    const approval_confirm = (checked: boolean) => {
      //approval_confirm, approval_confirm_by_opinion
      if (checked) {
        turnOnList.push('approval_confirm', 'approval_confirm_by_opinion');
      } else {
        turnOffList.push('approval_confirm', 'approval_confirm_by_opinion');
      }
    };
    const approval_reject = (checked: boolean) => {
      //approval_reject
      if (checked) {
        turnOnList.push('approval_reject');
      } else {
        turnOffList.push('approval_reject');
      }
    };
    const approval_process = (checked: boolean) => {
      //approval_process
      if (checked) {
        turnOnList.push('approval_process');
      } else {
        turnOffList.push('approval_process');
      }
    };
    const approval_reject_request = (checked: boolean) => {
      //approval_reject_request
      if (checked) {
        turnOnList.push('approval_reject_request');
      } else {
        turnOffList.push('approval_reject_request');
      }
    };
    const approval_confirm_referee = (checked: boolean) => {
      //approval_confirm_referee, approval_confirm_referee_by_opinion
      if (checked) {
        turnOnList.push(
          'approval_confirm_referee',
          'approval_confirm_referee_by_opinion',
        );
      } else {
        turnOffList.push(
          'approval_confirm_referee',
          'approval_confirm_referee_by_opinion',
        );
      }
    };
    const approval_add_referee = (checked: boolean) => {
      //approval_add_referee, approval_add_referee_by_opinion
      if (checked) {
        turnOnList.push(
          'approval_add_referee',
          'approval_add_referee_by_opinion',
        );
      } else {
        turnOffList.push(
          'approval_add_referee',
          'approval_add_referee_by_opinion',
        );
      }
    };

    const name = event.target.name;
    const turnOnList: string[] = [];
    const turnOffList: string[] = [];

    switch (name) {
      case 'all': {
        approval_opinion_attach(checked);
        approval_hold(checked);
        approval_confirm(checked);
        approval_reject(checked);
        approval_process(checked);
        approval_reject_request(checked);
        approval_add_referee(checked);
        approval_confirm_referee(checked);
        (checked ? turnOnList : turnOffList).push(
          'approval_cancel',
          'approval_cancel_referee',
          'approval_delay_reminder',
        );
        ATTENDANCE_KEYS.forEach((key) =>
          (checked ? turnOnList : turnOffList).push(key),
        );
        break;
      }
      case 'approval_opinion_attach': {
        approval_opinion_attach(checked);
        break;
      }
      case 'approval_hold': {
        approval_hold(checked);
        break;
      }
      case 'approval_confirm': {
        approval_confirm(checked);
        break;
      }
      case 'approval_reject': {
        approval_reject(checked);
        break;
      }
      case 'approval_process': {
        approval_process(checked);
        break;
      }
      case 'approval_reject_request': {
        approval_reject_request(checked);
        break;
      }
      case 'approval_add_referee': {
        approval_add_referee(checked);
        break;
      }
      case 'approval_confirm_referee': {
        approval_confirm_referee(checked);
        break;
      }
      case 'approval_cancel':
      case 'approval_cancel_referee':
      case 'approval_delay_reminder':
      case 'approval_confirm_annual_leave_occurred':
      case 'approval_confirm_working_hours_changed':
      case 'approval_confirm_overtime_occurred':
      case 'approval_confirm_commute_adjusted': {
        (checked ? turnOnList : turnOffList).push(name);
        break;
      }
      case 'jira': {
        if (checked) {
          turnOnList.push('jira');
        } else {
          turnOffList.push('jira');
        }
      }
    }

    setIsLoading(true);

    try {
      if (turnOnList.length > 0) {
        await updateMyPortalOptions({
          activated: 1,
          templateKeys: turnOnList,
        });
      }

      if (turnOffList.length > 0) {
        await updateMyPortalOptions({
          activated: 0,
          templateKeys: turnOffList,
        });
      }

      await updateNotiList();
      showSuccess({ message: t('MYPAGE.알림 설정이 업데이트 되었습니다.') });
    } catch (e) {
      const error = e as any;
      showError({
        message: error?.response?.data?.messsage ?? t('에러가 발생했습니다.'),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const alarmOptionOnChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    value: string,
  ) => {
    setIsLoading(true);

    try {
      const name = event.target.name;

      const alwaysTemplateKey = `${name}`;
      const opinionTemplateKey = `${name}_by_opinion`;

      if (value === 'always') {
        await Promise.all([
          updateMyPortalOption({
            activated: 1,
            templateKey: alwaysTemplateKey,
          }),
          updateMyPortalOption({
            activated: 1,
            templateKey: opinionTemplateKey,
          }),
        ]);
      } else {
        await Promise.all([
          updateMyPortalOption({
            activated: 0,
            templateKey: alwaysTemplateKey,
          }),
          updateMyPortalOption({
            activated: 1,
            templateKey: opinionTemplateKey,
          }),
        ]);
      }

      await updateNotiList();
      showSuccess({ message: t('MYPAGE.알림 설정이 업데이트 되었습니다.') });
    } catch (e) {
      const error = e as any;
      showError({
        message: error?.response?.data?.messsage ?? t('에러가 발생했습니다.'),
      });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    approvalSummary,
    isJiraActivated: jiraActivated,
    jiraServiceName,
    onClickApprovalMenu,
    isApprovalOpen,
    onBackDropClick: () => {
      setIsApprovalOpen(false);
    },
    approvalOnBack: () => {
      setIsApprovalOpen(false);
    },
    approvalOnClose: settings.menuOnClose,
    handleChangeAlarm,
    alarmOptionOnChange,
    isLoading,
    approvalValues: switchValue,
  };
};

export default useAlarmSettingProps;
